import { useList, useDelete, useUpdate } from "@refinedev/core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  User,
  ArrowUpDown,
  BookOpen,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FormHeader } from "@/components/forms";
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from "@/components/tables";
import { ReorderModal } from "@/components/modals";
import { type Teacher, getTeacherName } from './types';
import { TeacherCoursesDialog } from './TeacherCoursesDialog';
import { notify } from '@/lib/notify';

/**
 * Форматирование даты
 */
const dateFormatter = new Intl.DateTimeFormat("ru-RU", {
  dateStyle: "medium",
});

const formatDate = (value: string | null | undefined) => {
  if (!value) {return "—";}
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : dateFormatter.format(date);
};

/**
 * Бейдж статуса
 */
function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <Badge variant={isActive ? "default" : "secondary"}>
      {isActive ? "Активен" : "Неактивен"}
    </Badge>
  );
}

/**
 * Получить роль учителя
 */
function getTeacherRole(teacher: Teacher, lang: "ru" | "en" | "uz" = "ru"): string {
  return teacher.translations?.find((t) => t.languageCode === lang)?.role ?? "—";
}

/**
 * Страница списка учителей
 */
export function TeachersList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState | undefined>({ field: "order", order: "asc" });
  const [deleteItems, setDeleteItems] = useState<Teacher[]>([]);
  const [reorderOpen, setReorderOpen] = useState(false);
  const [relationsDialog, setRelationsDialog] = useState<{ teacher: Teacher; tab: string } | null>(null);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const pageSize = 20;

  // Собираем фильтры
  const filters: Array<{ field: string; operator: "contains" | "eq"; value: string | boolean }> = [];
  if (search) {
    filters.push({ field: "search", operator: "contains", value: search });
  }
  if (activeFilter !== "all") {
    filters.push({ field: "isActive", operator: "eq", value: activeFilter === "true" });
  }

  const { result, query } = useList<Teacher>({
    resource: "teachers",
    pagination: { currentPage: page, pageSize, mode: "server" },
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
    filters,
  });

  const teachers = result?.data ?? [];
  const total = result?.total ?? 0;
  const isLoading = query?.isLoading ?? false;

  const { mutate: deleteTeacher } = useDelete();
  const { mutate: updateTeacher } = useUpdate();

  const handleToggleActive = (items: Teacher[]) => {
    if (items.length === 0) {return;}
    items.forEach(item => {
      updateTeacher({
        resource: "teachers",
        id: item.id,
        values: { isActive: !item.isActive },
        successNotification: false,
        errorNotification: false,
      });
    });
    const activating = items.every(i => !i.isActive);
    notify.success(
      activating ? `Активировано: ${items.length}` : `Деактивировано: ${items.length}`,
    );
  };

  const handleDelete = () => {
    if (deleteItems.length === 0) {return;}

    for (const item of deleteItems) {
      deleteTeacher({
        resource: "teachers",
        id: item.id,
        successNotification: false,
        errorNotification: false,
      });
    }
    notify.success(`Удалено: ${deleteItems.length}`);
    setDeleteItems([]);
  };

  const columns: Column<Teacher>[] = [
    {
      key: "name",
      header: "Учитель",
      sortable: false,
      render: (_, row) => (
        <div className="flex items-center gap-3">
          {row.photoUrl ? (
            <img
              src={row.photoUrl}
              alt=""
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
              <User className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="font-medium text-sm">{getTeacherName(row)}</span>
            <span className="text-xs text-muted-foreground">{getTeacherRole(row)}</span>
          </div>
        </div>
      ),
    },
    {
      key: "courses",
      header: "Курсы",
      width: "70px",
      hiddenOnMobile: true,
      render: (_, row) => (
        <button
          type="button"
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            setRelationsDialog({ teacher: row, tab: 'courses' });
          }}
        >
          <BookOpen className="h-4 w-4" />
          <span>{row._count?.courses ?? 0}</span>
        </button>
      ),
    },
    {
      key: "posts",
      header: "Посты",
      width: "70px",
      hiddenOnMobile: true,
      render: (_, row) => (
        <button
          type="button"
          className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
          onClick={(e) => {
            e.stopPropagation();
            setRelationsDialog({ teacher: row, tab: 'posts' });
          }}
        >
          <FileText className="h-4 w-4" />
          <span>{row._count?.posts ?? 0}</span>
        </button>
      ),
    },
    {
      key: "isActive",
      header: "Статус",
      sortable: true,
      render: (_, row) => <StatusBadge isActive={row.isActive} />,
    },
    {
      key: "createdAt",
      header: "Создан",
      sortable: true,
      render: (val) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(typeof val === "string" ? val : null)}
        </span>
      ),
    },
    {
      key: "updatedAt",
      header: "Обновлён",
      sortable: true,
      render: (val) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(typeof val === "string" ? val : null)}
        </span>
      ),
    },
  ];

  const bulkActions: BulkAction<Teacher>[] = [
    {
      label: "Редактировать",
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          navigate(`/teachers/edit/${items[0].id}`);
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: "Активировать",
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter(i => !i.isActive)),
      showOnlyWhenSelected: true,
    },
    {
      label: "Деактивировать",
      icon: <EyeOff className="h-4 w-4" />,
      onClick: (items) => handleToggleActive(items.filter(i => i.isActive)),
      variant: "outline",
      showOnlyWhenSelected: true,
    },
    {
      label: "Удалить",
      icon: <Trash2 className="h-4 w-4" />,
      onClick: setDeleteItems,
      variant: "destructive",
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Учителя"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setReorderOpen(true)}>
              <ArrowUpDown className="h-4 w-4 mr-2" />
              Порядок
            </Button>
            <Button onClick={() => navigate("/teachers/create")}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить
            </Button>
          </div>
        }
        sticky
      />

      <div className="flex flex-wrap items-center gap-4">
        <TableToolbar
          searchValue={search}
          onSearchChange={(v) => { setSearch(v); setPage(1); }}
          searchPlaceholder="Поиск по имени..."
        />
        <Select
          value={activeFilter}
          onValueChange={(v) => { setActiveFilter(v); setPage(1); }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Все статусы" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все статусы</SelectItem>
            <SelectItem value="true">Активные</SelectItem>
            <SelectItem value="false">Неактивные</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={teachers}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(row) => navigate(`/teachers/edit/${row.id}`)}
        emptyMessage="Нет учителей"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      <AlertDialog open={deleteItems.length > 0} onOpenChange={() => setDeleteItems([])}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteItems.length === 1
                ? "Удалить учителя?"
                : `Удалить ${deleteItems.length} учителей?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteItems.length === 1 ? (
                <>
                  Вы уверены, что хотите удалить учителя «{getTeacherName(deleteItems[0])}»?
                  Это действие нельзя отменить.
                </>
              ) : (
                <>
                  Вы уверены, что хотите удалить {deleteItems.length} учителей?
                  Это действие нельзя отменить.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <TeacherCoursesDialog
        teacher={relationsDialog?.teacher ?? null}
        defaultTab={relationsDialog?.tab ?? 'courses'}
        open={relationsDialog !== null}
        onOpenChange={(v) => { if (!v) {setRelationsDialog(null);} }}
        onReassigned={() => query.refetch()}
      />

      <ReorderModal
        open={reorderOpen}
        onOpenChange={setReorderOpen}
        resource="teachers"
        items={teachers}
        title="Порядок учителей"
        renderItem={(teacher) => (
          <div className="flex items-center gap-3">
            {teacher.photoUrl ? (
              <img
                src={teacher.photoUrl}
                alt=""
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <div>
              <p className="font-medium text-sm">{getTeacherName(teacher)}</p>
              <p className="text-xs text-muted-foreground">{getTeacherRole(teacher)}</p>
            </div>
          </div>
        )}
        onSuccess={() => query.refetch()}
      />
    </div>
  );
}
